import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Listing, ListingStatus } from '../types';
import OptimizedImage from './OptimizedImage';
import FavoriteButton from './favorites/FavoriteButton';
import { getTimeAgo } from '../lib/dateUtils';
import { MessageSquare, HandCoins, Heart, Sparkles, ShoppingCart } from 'lucide-react';
import { motion } from 'framer-motion';

import { useAuth } from '../contexts/AuthContext';
import { useRegion } from '../src/contexts/RegionContext';

interface ListingCardProps {
    listing: Listing;
    showFavoriteButton?: boolean;
    priority?: boolean;
    children?: React.ReactNode;
    isVacationMode?: boolean;
}

const ListingCard: React.FC<ListingCardProps> = ({
    listing,
    showFavoriteButton = true,
    priority = false,
    children,
    isVacationMode = false
}) => {
    const navigate = useNavigate();
    const { currentUser } = useAuth();
    const { t } = useTranslation('marketplace');
    const { formatCurrency } = useRegion();

    const isBoosted = (listing.boostedUntil && listing.boostedUntil > Date.now()) || (listing as any).dopingActive;
    const isSold = listing.status === ListingStatus.SOLD;

    const conditionLabel = t(`listingForm.conditions.${listing.condition}`) || listing.condition;

    // 🎨 Condition-based color mapping
    const getConditionColor = (cond: string) => {
        switch (cond) {
            case 'new': return 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30';
            case 'like_new': return 'bg-cyan-500/20 text-cyan-400 border-cyan-500/30';
            case 'used': return 'bg-amber-500/20 text-amber-500 border-amber-500/30';
            default: return 'bg-white/10 text-white border-white/20';
        }
    };

    // 🛡️ Robust Image Resolution
    const getImages = (l: any): string[] => {
        const candidates = [l.imageUrls, l.image_urls, l.images, l.image, l.thumbnail, l.main_image, l.item_image];
        for (const c of candidates) {
            if (Array.isArray(c) && c.length > 0) return c.filter(Boolean);
            if (typeof c === 'string' && c.length > 0) return [c];
        }
        return [];
    };

    const images = getImages(listing);
    const mainImage = images[0] || 'https://via.placeholder.com/400x600?text=Resim+Yok';
    const timeAgo = getTimeAgo(listing.createdAt || (listing as any).created_at);

    // Location Formatting
    const locationStr = [listing.city, listing.district].filter(Boolean).join(', ') || 'Türkiye';

    const discountPercent = listing.originalPrice && listing.originalPrice > listing.price
        ? Math.round(((listing.originalPrice - listing.price) / listing.originalPrice) * 100)
        : 0;

    const handleQuickMessage = (e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();
        if (!currentUser) return navigate('/auth');
        navigate(`/listing/${listing.id}?chat=true`);
    };

    const handleQuickOffer = (e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();
        if (!currentUser) return navigate('/auth');
        navigate(`/listing/${listing.id}?offer=true`);
    };

    const handleBuyNow = (e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();
        if (!currentUser) return navigate('/auth');
        navigate(`/listing/${listing.id}?buy=true`);
    };

    return (
        <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            whileHover={{ y: -8, transition: { duration: 0.4, ease: [0.16, 1, 0.3, 1] } }}
            className="group relative h-full w-full rounded-[2.5rem] overflow-hidden shadow-premium-shallow hover:shadow-premium transition-all duration-700 bg-brand-white"
        >
            <Link to={`/listing/${listing.id}`} className="block h-full">
                {/* Full-Bleed Image Section */}
                <div className="relative aspect-[3/4] w-full overflow-hidden">
                    <motion.div
                        whileHover={{ scale: 1.08 }}
                        transition={{ duration: 1.2, ease: [0.16, 1, 0.3, 1] }}
                        className="w-full h-full"
                    >
                        <OptimizedImage
                            src={mainImage}
                            alt={listing.title}
                            className={`w-full h-full object-cover transition-all duration-700 
                                ${isVacationMode ? 'opacity-50 grayscale' : ''} 
                                brightness-[0.94] contrast-[1.05]
                                group-hover:brightness-105 group-hover:contrast-100
                            `}
                            priority={priority}
                            variant="listing-grid"
                        />
                    </motion.div>

                    {/* Sophisticated Gradients */}
                    <div className="absolute inset-x-0 bottom-0 h-1/2 bg-gradient-to-t from-black/90 via-black/30 to-transparent pointer-events-none" />
                    <div className="absolute inset-x-0 top-0 h-32 bg-gradient-to-b from-black/50 to-transparent pointer-events-none" />

                    {/* Condition Badge (Top Left) */}
                    <motion.div
                        initial={{ opacity: 0, x: -10 }}
                        animate={{ opacity: 1, x: 0 }}
                        className="absolute top-5 left-5 z-10 flex flex-col gap-2"
                    >
                        <div className={`backdrop-blur-xl px-3 py-1.5 rounded-2xl border ${getConditionColor(listing.condition)} shadow-lg`}>
                            <span className="text-[10px] font-black tracking-widest uppercase truncate max-w-[80px] block">
                                {conditionLabel}
                            </span>
                        </div>

                        {/* Discount Badge - Animated Reveal */}
                        {discountPercent > 0 && (
                            <div className="opacity-0 group-hover:opacity-100 translate-y-2 group-hover:translate-y-0 transition-all duration-700 delay-75">
                                <motion.div
                                    animate={{
                                        scale: [1, 1.02, 1],
                                        rotate: [-5, -6, -5]
                                    }}
                                    transition={{
                                        duration: 3,
                                        repeat: Infinity,
                                        ease: "easeInOut"
                                    }}
                                    className="bg-brand-accent px-2.5 py-1 rounded-xl shadow-2xl border border-white/20 transform"
                                >
                                    <span className="text-[11px] font-black text-gray-950 uppercase tracking-tight">
                                        %{discountPercent} {t('listingCard.discount')}
                                    </span>
                                </motion.div>
                            </div>
                        )}
                    </motion.div>

                    {/* Favorite Button (Top Right) - RESTORED & FIXED */}
                    {showFavoriteButton && (
                        <div
                            className="absolute top-4 right-4 z-50"
                            onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                            }}
                        >
                            <FavoriteButton
                                listingId={listing.id}
                                listingData={listing}
                                size="sm"
                                className="bg-white/20 backdrop-blur-3xl hover:bg-white/40 text-white border border-white/30 rounded-2xl w-12 h-12 flex items-center justify-center transition-all duration-300 active:scale-90 shadow-2xl"
                            />
                        </div>
                    )}

                    {/* Premium Seller Badge (Bottom Right Above Box) */}
                    <div className="absolute bottom-24 right-5 z-20 flex flex-col items-end gap-2 translate-y-4 opacity-0 group-hover:translate-y-0 group-hover:opacity-100 transition-all duration-500">
                        {/* Mini Seller Profile */}
                        <div className="bg-white/10 backdrop-blur-3xl border border-white/20 p-1.5 rounded-2xl flex items-center gap-2 pr-3 shadow-2xl">
                            <div className="w-6 h-6 rounded-lg bg-gray-500 overflow-hidden border border-white/30">
                                <img src={`https://ui-avatars.com/api/?name=${listing.sellerId}&background=random&color=fff`} className="w-full h-full object-cover" alt="" />
                            </div>
                            <div className="flex items-center gap-1">
                                <span className="text-[9px] font-black text-white/90">4.9</span>
                                <span className="text-amber-400 text-[8px]">★</span>
                            </div>
                        </div>
                    </div>

                    {/* INFO SHELF - The Blurred Base */}
                    <div className="absolute inset-x-0 bottom-0 p-5 pt-8 pointer-events-none">
                        <div className="flex flex-col gap-1">
                            <h3 className="text-white text-base font-bold leading-tight line-clamp-1 drop-shadow-md">
                                {listing.title}
                            </h3>

                            <div className="flex items-center justify-between mt-1 gap-2">
                                <div className="flex items-center gap-2 overflow-hidden flex-1">
                                    <span className="text-white/50 text-[10px] uppercase font-black tracking-widest truncate">
                                        {locationStr}
                                    </span>
                                </div>

                                {/* Price Tag - Scalable & Overflow Protected */}
                                <motion.div
                                    whileHover={{ scale: 1.05 }}
                                    className="bg-white/10 backdrop-blur-3xl px-3 py-1.5 rounded-xl border border-white/20 shadow-xl pointer-events-auto flex items-center gap-1.5 shrink-0 max-w-[65%]"
                                >
                                    {listing.originalPrice && listing.originalPrice > listing.price && (
                                        <span className="text-[10px] font-bold text-white/50 line-through tracking-tighter truncate">
                                            {formatCurrency(listing.originalPrice)}
                                        </span>
                                    )}
                                    <span className="text-[13px] font-black text-white tracking-tighter whitespace-nowrap">
                                        {formatCurrency(listing.price)}
                                    </span>
                                </motion.div>
                            </div>
                        </div>
                    </div>

                    {/* Hover Reveal Actions - Row of 3 Icons */}
                    <div className="absolute inset-0 bg-black/50 backdrop-blur-[4px] opacity-0 group-hover:opacity-100 transition-all duration-500 flex items-center justify-center z-40">
                        <div className="flex items-center justify-center gap-4 p-6 w-full">
                            {!isSold && (
                                <>
                                    <motion.button
                                        whileTap={{ scale: 0.9 }}
                                        onClick={handleBuyNow}
                                        className="w-14 h-14 bg-white text-gray-950 rounded-[1.5rem] flex items-center justify-center shadow-2xl hover:bg-brand-accent transition-all"
                                        title={t('listingCard.buyNow')}
                                    >
                                        <ShoppingCart size={22} strokeWidth={2.5} />
                                    </motion.button>

                                    {!listing.offersDisabled && (
                                        <motion.button
                                            whileTap={{ scale: 0.9 }}
                                            onClick={handleQuickOffer}
                                            className="w-14 h-14 bg-white/10 backdrop-blur-3xl border border-white/20 rounded-[1.5rem] flex items-center justify-center hover:bg-brand-accent hover:text-gray-950 text-white transition-all shadow-2xl"
                                            title={t('listingCard.makeOffer')}
                                        >
                                            <HandCoins size={22} strokeWidth={2.5} />
                                        </motion.button>
                                    )}

                                    <motion.button
                                        whileTap={{ scale: 0.9 }}
                                        onClick={handleQuickMessage}
                                        className="w-14 h-14 bg-white/10 backdrop-blur-3xl rounded-[1.5rem] flex items-center justify-center border border-white/20 hover:bg-white/20 text-white transition-all shadow-2xl"
                                        title={t('listingCard.sendMessage')}
                                    >
                                        <MessageSquare size={22} strokeWidth={2.5} />
                                    </motion.button>
                                </>
                            )}
                        </div>
                    </div>

                    {/* SOLD Overlay */}
                    {isSold && (
                        <div className="absolute inset-0 bg-black/80 backdrop-blur-md flex items-center justify-center z-50">
                            <span className="text-white text-2xl font-black uppercase tracking-[0.4em] drop-shadow-2xl">
                                {t('outOfStock')}
                            </span>
                        </div>
                    )}

                    {/* Vacation Mode */}
                    {isVacationMode && (
                        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-50 bg-amber-500/90 backdrop-blur-xl text-white px-6 py-3 rounded-2xl shadow-2xl">
                            <span className="text-sm font-black uppercase tracking-widest">🏖️ {t('listingCard.vacationMode')}</span>
                        </div>
                    )}
                </div>

                {/* Custom Actions Slot */}
                {children && (
                    <div className="absolute top-20 right-4 z-20 flex flex-col gap-2 opacity-0 group-hover:opacity-100 transition-all">
                        {children}
                    </div>
                )}
            </Link>
        </motion.div>
    );
};

export default ListingCard;
